import { TextField, TextFieldInputProps } from "@hex-insights/forms";
import { RadioRowInput } from "../../../Components/RadioRowInput";
import { AddressFormValues } from "../../FormUtilities";
import { CountrySelectField } from "../Country";
import { PersonSelectField } from "../Person";
import { BaseFieldProps } from "../shared";

/**
 * Generic props for fields of the Address model.
 */
type FieldProps<K extends keyof AddressFormValues.Base = keyof AddressFormValues.Base> = BaseFieldProps<
	Pick<AddressFormValues.Base, K>
>;

/**
 * Field component for the `name` field of the Address model.
 */
export function Name({ formState, ...props }: FieldProps<"name">) {
	return <TextField formState={formState} name="name" optional Input={NameInput} {...props} />;
}

const nameOptions = [{ value: "Home" }, { value: "Office" }];

function NameInput(props: TextFieldInputProps) {
	return <RadioRowInput options={nameOptions} {...props} className={undefined} />;
}

/**
 * Field component for the `addressLine1` field of the Address model.
 */
export function AddressLine1({ formState, ...props }: FieldProps<"addressLine1">) {
	return <TextField formState={formState} name="addressLine1" {...props} />;
}

/**
 * Field component for the `addressLine2` field of the Address model.
 */
export function AddressLine2({ formState, ...props }: FieldProps<"addressLine2">) {
	return <TextField formState={formState} name="addressLine2" optional {...props} />;
}

/**
 * Field component for the `city` field of the Address model.
 */
export function City({ formState, ...props }: FieldProps<"city">) {
	return <TextField formState={formState} name="city" {...props} />;
}

/**
 * Field component for the `postalCode` field of the Address model.
 */
export function PostalCode({ formState, ...props }: FieldProps<"postalCode">) {
	return <TextField formState={formState} name="postalCode" optional {...props} />;
}

/**
 * Field component for the `stateOrProvince` field of the Address model.
 */
export function StateOrProvince({ formState, ...props }: FieldProps<"stateOrProvince">) {
	return <TextField formState={formState} name="stateOrProvince" optional {...props} />;
}

export namespace Country {
	export type SelectProps = FieldProps<"countryID"> &
		Omit<CountrySelectField.SelectProps<AddressFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `country` edge of the Address model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <CountrySelectField.Select formState={formState} name="countryID" {...props} />;
	}
}

export namespace People {
	export type SelectProps = FieldProps<"personIDs"> &
		Omit<PersonSelectField.MultiSelectProps<AddressFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `people` edge of the Address model.
	 */
	export function Select({ formState, hint = "People who live at or work at this address.", ...props }: SelectProps) {
		return <PersonSelectField.MultiSelect formState={formState} name="personIDs" hint={hint} {...props} />;
	}
}
